// Copyright 2022, Imprivata, Inc.  All rights reserved.

import {
  createFaceCapture,
  type FaceCapture,
  CancellationError,
  type DetectionResult,
  type Face,
  type ValidnessResult,
  type ValidnessSettings,
} from '@imprivata-cloud/face-capture';
import type { ICameraFeedback, IDeviceData } from '../models';
import { CameraFeedback } from '../models';
import type { IFaceCaptureService } from './IFaceCaptureService';

const alignFaceWithGuideMessage = 'Align face with the guide';

export class LibraryFaceCaptureService implements IFaceCaptureService {
  private faceCapture: FaceCapture | null = null;
  private video: HTMLVideoElement | null = null;
  private videoStream: MediaStream | null = null;
  private captureAbortController: AbortController | null = null;
  private initializationPromise: Promise<void> | null = null;
  private isInitialized = false;

  private readonly sdkInitializationTimeout = 15000; // 15 seconds timeout for SDK initialization

  constructor(
    private readonly setCapturedAction: (captured: boolean) => void,
    private readonly setCapturingAction: (capturing: boolean) => void,
    private readonly setDeviceDataAction: (deviceData: IDeviceData) => void,
    private readonly setFeedbackAction: (feedback: ICameraFeedback) => void,
    private readonly enableDebugLogging = false,
  ) {}

  public async initialize(): Promise<void> {
    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] initialize() called');
    }

    // Return existing initialization promise if already initializing
    if (this.initializationPromise) {
      if (this.enableDebugLogging) {
        console.log('[LibraryFaceCaptureService] Returning existing initialization promise');
      }
      return this.initializationPromise;
    }

    // Return immediately if already initialized
    if (this.isInitialized) {
      if (this.enableDebugLogging) {
        console.log('[LibraryFaceCaptureService] Already initialized, returning');
      }
      return Promise.resolve();
    }

    // Create and store the initialization promise
    this.initializationPromise = this.performInitialization();

    try {
      return await this.initializationPromise;
    } catch (error) {
      // Reset initialization promise on failure to allow retry
      this.initializationPromise = null;
      if (this.enableDebugLogging) {
        console.error('[LibraryFaceCaptureService] Initialization failed:', error);
      }
      throw error;
    }
  }

  private async performInitialization(): Promise<void> {
    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] Starting library initialization with timeout:', this.sdkInitializationTimeout);
    }

    try {
      // Initialize the face capture library with timeout protection
      if (this.enableDebugLogging) {
        console.log('[LibraryFaceCaptureService] Calling createFaceCapture...');
      }

      this.faceCapture = await this.initializeWithTimeout(
        createFaceCapture({
          optimizeModelsFetch: true,
          modelsRootPath: window.location.origin,
        }),
        this.sdkInitializationTimeout,
        'Face capture library initialization timed out',
      );

      if (this.enableDebugLogging) {
        console.log('[LibraryFaceCaptureService] Face capture library initialized successfully');
      }

      // Set initialized flag atomically with SDK assignment to prevent race conditions
      this.isInitialized = true;

      if (this.enableDebugLogging) {
        console.log('[LibraryFaceCaptureService] isInitialized flag set to true');
      }
    } catch (error) {
      console.error('Failed to initialize face capture library:', error);
      throw new Error(
        `Failed to initialize face recognition: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  private async initializeWithTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    timeoutMessage: string,
  ): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs)),
    ]);
  }

  public async startVideo(video: HTMLVideoElement): Promise<void> {
    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] startVideo() called, isInitialized:', this.isInitialized);
    }

    // Ensure initialization is complete before starting video
    if (!this.isInitialized) {
      throw new Error('Face capture service must be initialized before starting video');
    }

    this.setInstantFeedback(
      new CameraFeedback(
        alignFaceWithGuideMessage,
        'Center yourself in the frame and look at the camera',
      ),
    );

    await this.startVideoStream();

    if (!this.videoStream) {
      if (this.enableDebugLogging) {
        console.warn('[LibraryFaceCaptureService] Video stream not available after startVideoStream()');
      }
      return;
    }

    this.video = video;
    this.video.srcObject = this.videoStream;

    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] Video stream attached to video element');
    }
  }

  public stopVideo(): void {
    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] stopVideo() called');
    }

    // Cancel any ongoing capture
    if (this.captureAbortController) {
      this.captureAbortController.abort();
      this.captureAbortController = null;
    }

    // Always stop the video stream to prevent camera from staying active
    // even if srcObject was cleared or never attached (race during startVideoStream)
    this.stopVideoStream();

    // Clear the srcObject if video element exists
    if (this.video) {
      this.video.srcObject = null;
    }

    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] Video stream stopped and cleared');
    }
  }

  public async runLiveness(
    canvas: HTMLCanvasElement,
    captureWindowMs: number,
  ): Promise<void> {
    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] runLiveness() started', {
        captureWindowMs,
        hasVideo: !!this.video,
      });
    }

    if (!this.video) {
      if (this.enableDebugLogging) {
        console.warn('[LibraryFaceCaptureService] No video element available for liveness check');
      }
      return;
    }

    // Create abort controller for cancellation support
    this.captureAbortController = new AbortController();

    // Reset capture state
    this.setCapturedAction(false);

    if (!this.faceCapture) {
      throw new Error('Face capture not initialized');
    }

    // Track if we've started capturing (set to true when first face is detected)
    let hasStartedCapturing = false;

    try {
      // Capture from video using the library (following react-demo pattern with Angular values)
      const result = await this.faceCapture.captureFromVideo(
        this.video,
        {
          signal: this.captureAbortController.signal,
          captureWindowMs: captureWindowMs,
          // Validness options - only non-default values
          validnessOptions: {
            minFaceQuality: 0.65,
            maxFaceSizePct: 0.9,
            imageBoundaryHeightPct: 0.9,
            failFast: false,
          } as Partial<ValidnessSettings>,
          cropExpansionFactor: 2,
        },
        {
          onFaceDetected: (detectionResult: DetectionResult) => {
            if (this.enableDebugLogging) {
              console.log('[LibraryFaceCaptureService] Face detected:', detectionResult.faces?.length || 0);
            }

            if (!detectionResult.faces?.length) {
              this.setInstantFeedback(new CameraFeedback(alignFaceWithGuideMessage, alignFaceWithGuideMessage, false));
            } else {
              // Set capturing state to true when first face is detected
              if (!hasStartedCapturing) {
                hasStartedCapturing = true;
                this.setCapturingAction(true);
              }
            }
          },
          onFaceValidated: (validnessResult: ValidnessResult, _face: Face) => {
            if (this.enableDebugLogging) {
              console.log('[LibraryFaceCaptureService] Face validated:', {
                isValid: validnessResult.isValid,
                feedback: validnessResult.feedback,
              });
            }

            // Filter out TOO_MANY_FACES feedback
            const feedback = validnessResult.feedback.filter(
              (f: string) => f !== 'TOO_MANY_FACES'
            )[0];

            this.setCameraFeedback(feedback);
          },
        }
      );

      if (this.enableDebugLogging) {
        console.log('[LibraryFaceCaptureService] Capture successful:', {
          quality: result.face.quality,
          framesAnalyzed: result.framesAnalyzed,
          captureTimeMs: result.captureTimeMs,
          hasCroppedImage: !!result.croppedImage?.dataUrl,
        });
      }

      // Use the cropped image directly from the library result
      const croppedImageDataUrl = result.croppedImage?.dataUrl;
      if (!croppedImageDataUrl) {
        throw new Error('No cropped image in capture result');
      }

      // Store the cropped face in the canvas
      await this.storeImageInCanvas(canvas, croppedImageDataUrl);

      // Mark capture as complete
      this.setCapturedAction(true);
      this.setCapturingAction(false);
    } catch (error) {
      // Reset capturing state on error
      this.setCapturingAction(false);

      if (error instanceof CancellationError) {
        return;
      }
      this.setInstantFeedback(
        new CameraFeedback(
          'Capture failed',
          'Unable to capture photo. Please try again.',
          true,
        ),
      );

      throw error;
    } finally {
      this.captureAbortController = null;
    }
  }

  private setCameraFeedback(feedback?: string): void {
    let feedbackText = '';
    let feedbackTitle = '';
    let isError = false;

    switch (feedback) {
      case 'FACE_MASK_FOUND':
        feedbackTitle = 'Remove face covering';
        feedbackText = 'Remove any face coverings and look at the camera';
        break;
      case 'FACE_SHARPNESS_POOR':
        feedbackTitle = 'Remain still';
        feedbackText = 'Remain still and look at the camera';
        break;
      case 'IMG_LIGHTING_DARK':
      case 'IMG_LIGHTING_BRIGHT':
        feedbackTitle = 'Lighting issue';
        feedbackText =
          'There appears to be a problem with the lighting. Step closer to the kiosk or try checking in using another method';
        isError = true;
        break;
      default:
        feedbackTitle = 'Center face';
        feedbackText = 'Center yourself in the frame and look at the camera';
        break;
    }

    const cameraFeedback = new CameraFeedback(feedbackTitle, feedbackText, isError);
    this.setFeedbackAction(cameraFeedback);
  }

  private setInstantFeedback(feedback: CameraFeedback): void {
    this.setFeedbackAction(feedback);
  }

  private storeImageInCanvas(canvas: HTMLCanvasElement, base64: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        canvas.width = image.width;
        canvas.height = image.height;

        const context = canvas.getContext('2d');
        if (context) {
          context.drawImage(image, 0, 0);
        }

        if (this.enableDebugLogging) {
          console.log('[LibraryFaceCaptureService] Image stored in canvas');
        }
        resolve();
      };
      image.onerror = () => {
        reject(new Error('Failed to load image into canvas'));
      };
      image.src = base64;
    });
  }

  private async startVideoStream(): Promise<void> {
    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] Starting video stream with constraints:', {
        width: { min: 600, ideal: 1920, max: 3840 },
        height: { min: 400, ideal: 1080, max: 2160 },
      });
    }

    this.stopVideoStream();

    this.videoStream = await navigator.mediaDevices
      .getUserMedia({
        video: {
          width: {
            min: 600,
            ideal: 1920,
            max: 3840,
          },
          height: {
            min: 400,
            ideal: 1080,
            max: 2160,
          },
        },
        audio: false,
      })
      .then(async (stream) => {
        if (this.enableDebugLogging) {
          console.log('[LibraryFaceCaptureService] Video stream obtained successfully');
        }

        const tracks = stream.getTracks();
        for (const track of tracks) {
          track.addEventListener('ended', this.handleCameraNotFound);
        }
        const deviceData = await this.getDeviceData(stream);
        this.setDeviceDataAction(deviceData);
        return stream;
      })
      .catch((error) => {
        if (this.enableDebugLogging) {
          console.error('[LibraryFaceCaptureService] Failed to get video stream:', error);
        }

        this.setInstantFeedback(
          new CameraFeedback(
            'Camera unavailable',
            'We are unable to access the camera at this time, try again later',
            true,
          ),
        );

        return null;
      });
  }

  private readonly handleCameraNotFound = (): void => {
    this.setInstantFeedback(
      new CameraFeedback(
        'Camera unavailable',
        'We are unable to access the camera at this time, try again later',
        true,
      ),
    );
  };

  private stopVideoStream(): void {
    if (!this.videoStream) {
      return;
    }

    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] Stopping video stream tracks:', this.videoStream.getTracks().length);
    }

    for (const track of this.videoStream.getTracks()) {
      track.stop();
    }

    this.videoStream = null;

    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] Video stream stopped and nullified');
    }
  }

  private async getDeviceData(stream: MediaStream): Promise<IDeviceData> {
    const tracks = stream.getTracks();
    const devices = await navigator.mediaDevices.enumerateDevices();
    const settings = tracks[0].getSettings();
    const deviceInfo = devices.find((device) => device.deviceId === settings.deviceId);

    const deviceData = {
      deviceId: settings.deviceId,
      deviceName: deviceInfo?.label,
      frameRate: settings.frameRate ? Math.round(settings.frameRate) : 0,
      height: settings.height,
      width: settings.width,
    };

    if (this.enableDebugLogging) {
      console.log('[LibraryFaceCaptureService] Device data collected:', deviceData);
    }

    return deviceData;
  }
}
